import { Injectable } from '@angular/core';
import type { AccessControlRuleInterface } from '@zaoblako/shared/modules/security/dtos/access-control/access-control-rule.dto';
import type { RoomAccessFindInterface } from '@zaoblako/shared/modules/security/dtos/room-access/room-access-find.dto';
import type { RoomAccessFormInterface } from '@zaoblako/shared/modules/security/dtos/room-access/room-access-form.dto';
import { UserDeleteQueryInterface } from '@zaoblako/shared/modules/security/dtos/user/user-delete-query.dto';
import type { UserEmailInterface } from '@zaoblako/shared/modules/security/dtos/user/user-email.dto';
import type { UserFindAllQueryInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find-all-query.dto';
import type { UserFindQueryInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find-query.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { UserListFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-list-find.dto';
import { UserNeedDelegateInterface } from '@zaoblako/shared/modules/security/dtos/user/user-need-delegate.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseService {
  protected baseUrl = '/basis/security/user';

  public findEmployees(queryParams: UserFindQueryInterface): Observable<UserListFindInterface> {
    return this.http.get<UserListFindInterface>(`${this.baseUrl}/page`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public findAll(queryParams?: UserFindAllQueryInterface): Observable<UserFindInterface[]> {
    return this.http.get<UserFindInterface[]>(`${this.baseUrl}/flat`, { params: this.objectToHttpParams(queryParams) });
  }

  public restorePassword(body: UserEmailInterface): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/restore-password`, body);
  }

  public getAccessControlRules(): Observable<AccessControlRuleInterface[]> {
    return this.http.get<AccessControlRuleInterface[]>(`${this.baseUrl}/access-control-rules`);
  }

  public ban(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/ban`, null);
  }

  public unban(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/unban`, null);
  }

  public remove(id: number, queryParams?: UserDeleteQueryInterface): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`, { params: this.objectToHttpParams(queryParams) });
  }

  public getPacsAccess(id: number): Observable<RoomAccessFindInterface> {
    return this.http.get<RoomAccessFindInterface>(`${this.baseUrl}/${id}/pacs-access`);
  }

  public setPacsAccess(id: number, body: RoomAccessFormInterface): Observable<RoomAccessFindInterface> {
    return this.http.put<RoomAccessFindInterface>(`${this.baseUrl}/${id}/pacs-access`, body);
  }

  public needDelegate(id: number): Observable<UserNeedDelegateInterface> {
    return this.http.get<UserNeedDelegateInterface>(`${this.baseUrl}/${id}/need-delegate`);
  }
}
